import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import React, { useCallback } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import ImageUploadInput from 'modules/menu/components/ImageUploadInput';
import { VenueIsLinkedAlert } from 'modules/menu/components/VenueIsLinkedAlert';
import { useSharedMenuLink } from 'modules/menu/hooks/useSharedMenuLink';

import { CreateCategoryFormValues } from './types';
import { SubcategoryInputFields } from '../SubcategoryInputFields';

const StyledFormRow = styled(FormRow)`
  & + & {
    border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  }
`;

const StyledImageUploadInput = styled(ImageUploadInput)`
  margin-block-end: 16px;
  div[role='presentation'] {
    min-height: 80px;
  }
`;

type AddCategoryDetailsFormSectionProps = {
  hasCategoryImagesPermission: boolean;
  hasSubcategory: boolean;
  className?: string;
  onBackClick: () => void;
  onCloseClick: () => void;
  onSaveClick: () => void;
};

type SubcategoryInputFieldsComponentProps = {
  categoryRowId: string;
  childCategoriesRecord: CreateCategoryFormValues['childCategoriesRecord'];
};

const CreateCategoryFormFooter = styled(ModalFooter)``;

const BackButton = styled(Button)`
  margin-inline-start: auto;
`;

const SaveButton = styled(Button)`
  margin-inline-end: 72px;
  margin-inline-start: 16px;
`;

const AddCategoryFormSectionBody = styled(ModalContent)`
  flex: 1;
  overflow: auto;
`;

const Container = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const AddCategoryFormSectionRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SubmitError = styled(Alert).attrs({ variant: 'error' })`
  margin: 0 0 16px;
`;

const SubcategoryInputFieldsComponent = ({
  categoryRowId,
  childCategoriesRecord,
}: SubcategoryInputFieldsComponentProps) => {
  const subcategories = childCategoriesRecord[categoryRowId];
  const form = useForm<CreateCategoryFormValues>();
  const { fields } = useFieldArray<CreateCategoryFormValues['childCategoriesRecord']>(
    `childCategoriesRecord[${categoryRowId}]`,
  );

  const handleChange = useCallback(
    (data: CreateCategoryFormValues['childCategoriesRecord'][string]) => {
      const updatedSubcategoriesRecord = { ...childCategoriesRecord, [categoryRowId]: data };
      form.change('childCategoriesRecord', updatedSubcategoriesRecord);
    },
    [categoryRowId, childCategoriesRecord, form],
  );

  return <SubcategoryInputFields fields={fields} subcategories={subcategories} onChange={handleChange} />;
};

export const AddCategoryDetailsFormSection = ({
  onBackClick,
  onCloseClick,
  onSaveClick,
  className,
  hasCategoryImagesPermission,
  hasSubcategory,
}: AddCategoryDetailsFormSectionProps) => {
  const intl = useIntl();
  const { fields } = useFieldArray<CreateCategoryFormValues>('categories');
  const {
    hasValidationErrors,
    submitError,
    submitting,
    values: { categories, childCategoriesRecord },
  } = useFormState<CreateCategoryFormValues>();

  const { sharedMenuLink } = useSharedMenuLink();

  return (
    <AddCategoryFormSectionRoot className={className}>
      <ModalHeader onCloseClick={onCloseClick}>
        <FormattedMessage id="menu.bulkCreateCategories.addCategoryDetails.title" />
      </ModalHeader>
      {!!sharedMenuLink && <VenueIsLinkedAlert />}

      <AddCategoryFormSectionBody>
        <Container>
          {fields.map((name, categoryIndex) => {
            const category = categories[categoryIndex];

            if (!category || category.name.trim() === '') {
              return null;
            }

            return (
              <StyledFormRow key={name} horizontalPadding label={category.name}>
                {hasCategoryImagesPermission && (
                  <Field name={`${name}.image`}>{imageField => <StyledImageUploadInput {...imageField.input} />}</Field>
                )}

                <Field name={`${name}.description`}>
                  {descriptionField => (
                    <RFFFieldErrorWrapper field={descriptionField}>
                      {rffFieldErrorWrapperProps => (
                        <Input
                          {...rffFieldErrorWrapperProps}
                          placeholder={intl.formatMessage({
                            id: 'menu.bulkCreateCategories.addCategoryDetails.descriptionPlaceholder',
                          })}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>

                {hasSubcategory && (
                  <SubcategoryInputFieldsComponent
                    categoryRowId={category._rowId}
                    childCategoriesRecord={childCategoriesRecord}
                  />
                )}
              </StyledFormRow>
            );
          })}
          {submitError && <SubmitError>{submitError}</SubmitError>}
        </Container>
      </AddCategoryFormSectionBody>

      <CreateCategoryFormFooter>
        <BackButton variant="lightBlue" size="small" onClick={onBackClick}>
          <FormattedMessage id="menu.bulkCreateCategories.addCategoryDetails.back" />
        </BackButton>
        <SaveButton
          variant="blue"
          size="small"
          disabled={hasValidationErrors}
          loading={submitting}
          onClick={onSaveClick}
        >
          <FormattedMessage id="state.save" />
        </SaveButton>
      </CreateCategoryFormFooter>
    </AddCategoryFormSectionRoot>
  );
};
